/* Policies — Phase 2 item 2.6 UI (CGPL DSL v2, editable)
 *
 * v2 adds:
 *   • Create new policies (with category dropdown, when/then editor)
 *   • Edit existing policies (in-place YAML field editor)
 *   • Delete policies (with confirmation)
 *   • Validate (dry-run) a policy against a sample resource JSON
 *   • Export signed bundle / import signed bundle
 *   • Decision log tab (SOC2 evidence)
 *   • Run YAML test fixtures
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, ShieldCheck, AlertCircle, ChevronDown, ChevronUp,
  FileText, Layers, Plus, Edit3, Trash2, Save, Download, Upload, FlaskConical,
  PlayCircle, Eye, X, Activity, CheckCircle2, XCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { API } from '../../lib/api';

const RISK_COLOURS = {
  critical: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
  high:     'bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30',
  medium:   'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  low:      'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  info:     'bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30',
};

const CATEGORY_COLOURS = {
  cost: 'text-emerald-500', governance: 'text-blue-500',
  security: 'text-red-500', compliance: 'text-amber-500',
  database: 'text-purple-500',
};

const ALLOWED_CATEGORIES = ['cost', 'governance', 'security', 'compliance', 'database'];

// ── Empty starter for the editor ─────────────────────────────────────────
const blankPolicy = () => ({
  id: '', category: 'cost', risk: 'medium', description: '',
  when: [''],
  then: { recommend: '' },
  guard: [],
});

// ── Editor modal ─────────────────────────────────────────────────────────
function PolicyEditorModal({ open, onClose, initial, onSaved }) {
  const [policy, setPolicy] = useState(initial || blankPolicy());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [validateResult, setValidateResult] = useState(null);
  const [sampleJson, setSampleJson] = useState('{"type":"instance","state":"running","metrics":{"cpu_avg":4.2}}');

  useEffect(() => { setPolicy(initial || blankPolicy()); setErr(null); setValidateResult(null); }, [initial, open]);

  if (!open) return null;

  const isEdit = Boolean(initial?.id);

  const setField = (k, v) => setPolicy(prev => ({ ...prev, [k]: v }));
  const setWhen = (idx, v) => setPolicy(prev => {
    const w = [...(prev.when || [])]; w[idx] = v; return { ...prev, when: w };
  });
  const addWhen = () => setPolicy(prev => ({ ...prev, when: [...(prev.when || []), ''] }));
  const removeWhen = (idx) => setPolicy(prev => ({
    ...prev, when: (prev.when || []).filter((_, i) => i !== idx),
  }));
  const setThenField = (k, v) => setPolicy(prev => ({ ...prev, then: { ...prev.then, [k]: v } }));

  const save = async () => {
    setBusy(true); setErr(null);
    const body = { ...policy, when: (policy.when || []).filter(x => x.trim()) };
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/policies/${policy.id}`, body);
      } else {
        await axios.post(`${API}/admin/policies`, body);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const validate = async () => {
    setBusy(true); setErr(null); setValidateResult(null);
    try {
      const resource = JSON.parse(sampleJson);
      const r = await axios.post(`${API}/admin/policies/validate`, {
        policy: { ...policy, when: (policy.when || []).filter(x => x.trim()) },
        resource,
      });
      setValidateResult(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          data-testid="policy-editor-modal"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-background shadow-2xl">
        <div className="sticky top-0 bg-background border-b border-border px-5 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Edit3 className="w-4 h-4 text-primary" />
            {isEdit ? `Edit policy: ${initial?.id}` : 'Create new policy'}
          </h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-secondary" data-testid="policy-editor-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">ID (snake_case)</Label>
              <Input value={policy.id} disabled={isEdit}
                      onChange={(e) => setField('id', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                      data-testid="pe-id" />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={policy.category} onValueChange={(v) => setField('category', v)}>
                <SelectTrigger data-testid="pe-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALLOWED_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Risk</Label>
              <Select value={policy.risk} onValueChange={(v) => setField('risk', v)}>
                <SelectTrigger data-testid="pe-risk"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['info','low','medium','high','critical'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={policy.description || ''}
                      onChange={(e) => setField('description', e.target.value)}
                      data-testid="pe-description" />
            </div>
          </div>
          <div>
            <Label className="text-xs flex items-center justify-between">
              When clauses (CGPL expressions — ALL must be true)
              <Button size="sm" variant="ghost" onClick={addWhen} className="h-6 text-xs"
                       data-testid="pe-add-when">
                <Plus className="w-3 h-3 mr-1" /> add
              </Button>
            </Label>
            <div className="space-y-1.5">
              {(policy.when || []).map((w, i) => (
                <div key={i} className="flex gap-1.5">
                  <Input value={w} onChange={(e) => setWhen(i, e.target.value)}
                          className="font-mono text-xs"
                          placeholder='e.g. resource.type == "instance"'
                          data-testid={`pe-when-${i}`} />
                  <Button size="sm" variant="ghost" onClick={() => removeWhen(i)} className="h-9 px-2"
                           data-testid={`pe-remove-when-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Then.recommend (action verb)</Label>
            <Input value={policy.then?.recommend || ''}
                    onChange={(e) => setThenField('recommend', e.target.value)}
                    placeholder="rightsize | delete | stop | tag | lock_down"
                    data-testid="pe-then-recommend" />
          </div>
          <div>
            <Label className="text-xs">Then.reason (human-readable; supports {`{cpu_avg}`} placeholders)</Label>
            <Textarea value={policy.then?.reason || ''}
                      onChange={(e) => setThenField('reason', e.target.value)}
                      rows={2} className="font-mono text-xs"
                      data-testid="pe-then-reason" />
          </div>

          {/* Validate */}
          <div className="rounded-lg border border-border bg-secondary/20 p-3">
            <p className="text-xs font-semibold mb-1 flex items-center gap-1.5">
              <FlaskConical className="w-3.5 h-3.5 text-amber-500" />
              Dry-run against sample resource
            </p>
            <Textarea value={sampleJson} onChange={(e) => setSampleJson(e.target.value)}
                      rows={3} className="font-mono text-[11px]" data-testid="pe-validate-json" />
            <div className="flex items-center gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={validate} disabled={busy}
                       className="text-xs" data-testid="pe-validate-btn">
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PlayCircle className="w-3 h-3 mr-1" />}
                Test
              </Button>
              {validateResult && (
                <span className={`text-xs flex items-center gap-1 ${validateResult.fired
                  ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}
                      data-testid="pe-validate-result">
                  {validateResult.fired
                    ? <><CheckCircle2 className="w-3 h-3" /> Fires</>
                    : <><XCircle className="w-3 h-3" /> Does not fire</>}
                </span>
              )}
            </div>
          </div>

          {err && (
            <div className="text-xs text-red-600 dark:text-red-400 rounded border border-red-500/30 bg-red-500/5 px-2 py-1.5"
                 data-testid="pe-error">
              {err}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-background border-t border-border px-5 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="pe-save-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            {isEdit ? 'Save changes' : 'Create policy'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Decision-log drawer ──────────────────────────────────────────────────
function DecisionsDrawer({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/policies/decisions?limit=100`),
        axios.get(`${API}/admin/policies/decisions/summary`),
      ]);
      setItems(a.data?.items || []);
      setSummary(b.data || null);
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) fetch(); }, [open]);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          data-testid="decisions-drawer"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-4xl max-h-[85vh] overflow-y-auto rounded-xl border border-border bg-background shadow-2xl">
        <div className="sticky top-0 bg-background border-b border-border px-5 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" /> Decision log (SOC2 trail)
            {summary && <Badge variant="outline" className="text-[10px]">{summary.total || 0} total</Badge>}
          </h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-secondary"
                  data-testid="decisions-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4">
          {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-muted-foreground italic text-center py-6">
              No decisions logged yet. Run an evaluation from the policies list to populate.
            </p>
          )}
          {items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left py-1.5">When</th>
                    <th className="text-left py-1.5">Policy</th>
                    <th className="text-left py-1.5">Resource</th>
                    <th className="text-left py-1.5">Outcome</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {items.map((it, idx) => (
                    <tr key={idx} className="hover:bg-secondary/30">
                      <td className="py-1.5 text-muted-foreground tabular-nums whitespace-nowrap">
                        {new Date(it.created_at).toLocaleString()}
                      </td>
                      <td className="py-1.5 font-mono">{it.policy_id}</td>
                      <td className="py-1.5 font-mono text-muted-foreground truncate max-w-xs">
                        {it.resource_id}
                      </td>
                      <td className="py-1.5">
                        {it.fired
                          ? <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> fired</span>
                          : <span className="text-muted-foreground flex items-center gap-1"><XCircle className="w-3 h-3" /> no-fire</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────
export default function Policies() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [evaluating, setEvaluating] = useState(false);
  const [evalResult, setEvalResult] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPolicy, setEditorPolicy] = useState(null);
  const [decisionsOpen, setDecisionsOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/admin/policies`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const runEvaluation = async () => {
    setEvaluating(true);
    try {
      const r = await axios.get(`${API}/admin/policies?evaluate=true`);
      setEvalResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setEvaluating(false);
    }
  };

  const runTests = async () => {
    try {
      const r = await axios.post(`${API}/admin/policies/tests/run`);
      setTestResult(r.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const exportBundle = async () => {
    try {
      const r = await axios.get(`${API}/admin/policies/bundle`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cgpl-bundle-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const importBundle = async (file) => {
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const r = await axios.post(`${API}/admin/policies/bundle/import`, bundle);
      alert(`Imported ${r.data.imported} of ${r.data.total_in_bundle} policies.`);
      fetchData();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const deletePolicy = async (id) => {
    if (!window.confirm(`Delete policy '${id}'? This removes the YAML file from disk.`)) return;
    try {
      await axios.delete(`${API}/admin/policies/${id}`);
      fetchData();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  useEffect(() => { fetchData(); }, []);

  const policies = data?.policies || [];
  const fireCounts = evalResult?.fire_counts || evalResult?.counts || {};
  const fireCountByPolicy = evalResult ? Object.fromEntries(
    (evalResult.policies || []).map(p => [p.id, p.fire_count || 0])) : {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="policies-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Chivo'] flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-primary" />
            CGPL Policies
            <Badge variant="outline" className="text-[10px] ml-2">
              {data?.mode === 'v2_editable' ? 'v2 — editable' : 'v1 read-only'}
            </Badge>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            CloudGenie Policy Language — versioned YAML rules that drive
            deterministic recommendations. Edit, validate, bundle, and audit.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => { setEditorPolicy(null); setEditorOpen(true); }}
                  data-testid="policies-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New policy
          </Button>
          <Button size="sm" variant="outline" onClick={runEvaluation} disabled={evaluating}
                  data-testid="policies-evaluate-btn">
            {evaluating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Layers className="w-3.5 h-3.5 mr-1.5" />}
            Evaluate now
          </Button>
          <Button size="sm" variant="outline" onClick={runTests} data-testid="policies-tests-btn">
            <FlaskConical className="w-3.5 h-3.5 mr-1.5" /> Run tests
          </Button>
          <Button size="sm" variant="outline" onClick={() => setDecisionsOpen(true)}
                  data-testid="policies-decisions-btn">
            <Activity className="w-3.5 h-3.5 mr-1.5" /> Decision log
          </Button>
          <Button size="sm" variant="outline" onClick={exportBundle}
                  data-testid="policies-export-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export bundle
          </Button>
          <label className="cursor-pointer inline-block">
            <input type="file" accept="application/json" className="hidden"
                    onChange={(e) => e.target.files?.[0] && importBundle(e.target.files[0])}
                    data-testid="policies-import-input" />
            <span className="inline-flex items-center text-xs px-3 h-9 rounded-md border border-border hover:bg-secondary/40 cursor-pointer">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Import bundle
            </span>
          </label>
          <Button size="sm" variant="outline" onClick={fetchData} disabled={loading}
                  data-testid="policies-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {testResult && (
        <Card className={testResult.ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/40 bg-red-500/5'}>
          <CardContent className="p-3 text-xs flex items-center gap-2"
                       data-testid="policies-test-result">
            <FlaskConical className="w-3.5 h-3.5" />
            <span className="font-semibold">
              {testResult.passed} passed · {testResult.failed} failed
            </span>
            {testResult.failures?.length > 0 && (
              <span className="text-red-600 dark:text-red-400 truncate">
                {testResult.failures.join(' · ')}
              </span>
            )}
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {error && (
        <Card className="border-red-500/40 bg-red-500/5">
          <CardContent className="p-4 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Loaded policies</p>
              <p className="text-3xl font-bold text-primary mt-1" data-testid="policies-total">
                {data.total_policies || policies.length}
              </p>
              <p className="text-xs text-muted-foreground mt-1">YAML files parsed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Categories</p>
              <p className="text-3xl font-bold text-primary mt-1" data-testid="policies-categories">
                {[...new Set(policies.map(p => p.category))].length}
              </p>
              <p className="text-xs text-muted-foreground mt-1">cost · governance · security · compliance · database</p>
            </CardContent>
          </Card>
          <Card className={evalResult ? 'border-emerald-500/30 bg-emerald-500/5' : ''}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Last evaluation</p>
              <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-1"
                  data-testid="policies-eval-fires">
                {evalResult ? Object.values(fireCountByPolicy).reduce((a, b) => a + Number(b || 0), 0) : '—'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {evalResult ? 'total firings across resources' : 'Click “Evaluate now” to run rules'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {policies.map((p) => {
        const cat = p.category || 'uncategorised';
        const isOpen = !!expanded[p.id];
        const fires = Number(fireCountByPolicy[p.id] ?? 0);
        return (
          <Card key={p.id} data-testid={`policy-${p.id}`} className="overflow-hidden">
            <button
              onClick={() => setExpanded(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
              className="w-full text-left" data-testid={`policy-${p.id}-toggle`}>
              <CardHeader className="pb-3 hover:bg-secondary/30 transition-colors">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className={`w-4 h-4 ${CATEGORY_COLOURS[cat] || 'text-primary'}`} />
                    <code className="font-mono text-sm">{p.id}</code>
                    <span className="font-normal text-xs text-muted-foreground">— {p.description || p.subcategory || ''}</span>
                  </CardTitle>
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant="outline" className="text-[10px] uppercase">{cat}</Badge>
                    {p.risk && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${RISK_COLOURS[String(p.risk).toLowerCase()] || ''}`}>
                        {p.risk}
                      </span>
                    )}
                    {evalResult && (
                      <Badge variant="secondary" className="text-[10px]"
                              data-testid={`policy-${p.id}-fires`}>
                        {fires} fire{fires === 1 ? '' : 's'}
                      </Badge>
                    )}
                    {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </div>
                </div>
              </CardHeader>
            </button>
            {isOpen && (
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">When (conditions)</p>
                    <ul className="space-y-1">
                      {(p.when || []).map((w, i) => (
                        <li key={i} className="font-mono px-2 py-1 rounded bg-secondary/40 break-all">{w}</li>
                      ))}
                      {(p.when || []).length === 0 && <li className="text-muted-foreground italic">none</li>}
                    </ul>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Then (recommendation)</p>
                    <div className="font-mono px-2 py-1 rounded bg-secondary/40 space-y-1">
                      {p.then && Object.entries(p.then).map(([k, v]) => (
                        <div key={k}>
                          <span className="text-muted-foreground">{k}:</span> <span>{Array.isArray(v) ? v.join(', ') : String(v)}</span>
                        </div>
                      ))}
                    </div>
                    {p.guard && p.guard.length > 0 && (
                      <>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mt-3 mb-1">Guard (safety)</p>
                        <ul className="space-y-1">
                          {p.guard.map((g, i) => (
                            <li key={i} className="font-mono px-2 py-1 rounded bg-amber-500/10 border border-amber-500/20 break-all">{g}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-3 pt-3 border-t border-border">
                  <span>Source: <code className="font-mono">{p.path || '—'}</code></span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                             onClick={(e) => { e.stopPropagation(); setEditorPolicy(p); setEditorOpen(true); }}
                             data-testid={`policy-${p.id}-edit`}>
                      <Edit3 className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline"
                             className="h-7 text-xs text-red-600 dark:text-red-400 border-red-500/30 hover:bg-red-500/10"
                             onClick={(e) => { e.stopPropagation(); deletePolicy(p.id); }}
                             data-testid={`policy-${p.id}-delete`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      {!loading && !error && policies.length === 0 && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="p-6 text-center">
            <ShieldCheck className="w-10 h-10 text-amber-500 mx-auto mb-2 opacity-60" />
            <p className="text-sm font-semibold">No CGPL policies loaded</p>
            <p className="text-xs text-muted-foreground mt-1">
              Create your first policy or import a signed bundle.
            </p>
          </CardContent>
        </Card>
      )}

      <PolicyEditorModal
        open={editorOpen}
        initial={editorPolicy}
        onClose={() => setEditorOpen(false)}
        onSaved={fetchData}
      />
      <DecisionsDrawer open={decisionsOpen} onClose={() => setDecisionsOpen(false)} />
    </div>
  );
}
